import React, { useState, useEffect } from 'react';
import { FaFileExcel, FaFilePdf } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { CiSearch } from 'react-icons/ci';
import axios from 'axios';
import { BaseUrl } from '../App';
import { FadeLoader } from 'react-spinners';

function PaymentS() {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [paymentData, setPaymentData] = useState([]);
  const [userId, setuserId] = useState(null);

  // Fetch userId from sessionStorage
  useEffect(() => {
    const fetchedStaffId = sessionStorage.getItem('userId');
    if (fetchedStaffId) {
      setuserId(fetchedStaffId);
    }
  }, []);

  // Fetch payment data
  useEffect(() => {
    if (userId) {
      const fetchPaymentData = async () => {
        setLoading(true);
        try {
          const response = await axios.post(`${BaseUrl}/backend/api/payment_by_user_type.php`, {
            action: 'get_payment_particular_staff',
            api_key: '1',
            user_type: 'staff',
            user_id: userId,
          });

          if (response.data.status === 'true') {
            setPaymentData(response.data.response);
          }
        } catch (error) {
          console.error('Error fetching payment data:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchPaymentData();
    }
  }, [userId]);

  // Filter and paginate data
  const filteredData = paymentData.filter(
    (row) =>
      row.payment_method.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.payment_date.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const startIndex = (currentPage - 1) * entriesPerPage;
  const paginatedData =
    entriesPerPage === 'All' ? filteredData : filteredData.slice(startIndex, startIndex + entriesPerPage);
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);

  const handleSearch = (e) => setSearchQuery(e.target.value);
  const handleEntriesChange = (e) => {
    setEntriesPerPage(e.target.value === 'All' ? 'All' : parseInt(e.target.value, 10));
    setCurrentPage(1);
  };
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  // Excel Export
  const handleExcelExport = () => {
    const columns = ['ID', 'Payment Method', 'Payment Date', 'Amount', 'Advance Paid', 'Net Total'];
    const rows = paginatedData.map((row) => [
      row.id,
      row.payment_method,
      row.payment_date,
      row.amt_payable,
      row.advance_paid,
      row.net_total,
    ]);

    const ws = XLSX.utils.aoa_to_sheet([columns, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payments');
    XLSX.writeFile(wb, 'payment-data.xlsx');
  };

  // PDF Export
  const handlePdfExport = () => {
    const doc = new jsPDF();
    const columns = ['ID', 'Payment Method', 'Payment Date', 'Amount', 'Advance Paid', 'Net Total'];
    const rows = paginatedData.map((row) => [
      row.id,
      row.payment_method,
      row.payment_date,
      row.amt_payable,
      row.advance_paid,
      row.net_total,
    ]);

    doc.autoTable({
      head: [columns],
      body: rows,
      startY: 20,
      theme: 'grid',
      margin: { horizontal: 10 },
      headStyles: {
        fillColor: [0, 123, 255],
        textColor: 255,
      },
    });

    doc.save('payment-data.pdf');
  };

  return (
    <div className="relative min-h-screen bg-gray-50">
      {/* Loading Indicator */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="flex flex-col items-center p-6 bg-white rounded-2xl shadow-lg">
            <FadeLoader height={15} width={5} color="#3b82f6" ariaLabel="loading" />
            <h1 className="mt-2 text-lg font-medium text-gray-800">Please wait...</h1>
          </div>
        </div>
      )}  

      <div className="container mx-auto p-4 sm:p-6 xl:p-8">
        {/* Top Section: Search, Export Buttons, Entries Dropdown */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 space-y-4 sm:space-y-0">
          {/* Search and Export Buttons */}
          <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
            {/* Search Input */}
            <div className="relative w-full sm:w-80">
              <input
                type="text"
                placeholder="Search by method or date..."
                value={searchQuery}
                onChange={handleSearch}
                className="w-full py-2.5 pl-10 pr-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm transition duration-300"
              />
              <CiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            </div>

            {/* Export Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={handleExcelExport}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg shadow-md hover:bg-green-700 transition duration-300"
              >
                <FaFileExcel size={20} /> Excel
              </button>
              <button
                onClick={handlePdfExport}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg shadow-md hover:bg-red-700 transition duration-300"
              >
                <FaFilePdf size={20} /> PDF
              </button>
            </div>
          </div>

          {/* Entries Dropdown */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-600">Show</span>
            <select
              value={entriesPerPage}
              onChange={handleEntriesChange}
              className="py-2 px-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value="All">All</option>
            </select>
            <span className="text-sm font-medium text-gray-600">entries</span>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto bg-white rounded-lg shadow-lg">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-blue-600 text-white">
                <th className="py-3 px-4 text-left text-sm font-semibold rounded-tl-lg">#</th>
                <th className="py-3 px-4 text-left text-sm font-semibold">Payment Method</th>
                <th className="py-3 px-4 text-left text-sm font-semibold">Payment Date</th>
                <th className="py-3 px-4 text-left text-sm font-semibold">Amount</th>
                <th className="py-3 px-4 text-left text-sm font-semibold">Advance Paid</th>
                <th className="py-3 px-4 text-left text-sm font-semibold rounded-tr-lg">Net Total</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row, index) => (
                <tr
                  key={index}
                  className={`${
                    index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                  } hover:bg-blue-50 transition duration-200`}
                >
                  <td className="py-3 px-4 text-sm text-gray-700">{row.id}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">{row.payment_method}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">{row.payment_date}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">{row.amt_payable}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">{row.advance_paid}</td>
                  <td className="py-3 px-4 text-sm text-green-600 font-medium">{row.net_total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-4">
          {paginatedData.map((row, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-md p-5 border border-gray-100 hover:shadow-lg transition duration-200"
            >
              <div className="flex justify-between items-center mb-3 border-b pb-2">
                <span className="font-semibold text-lg text-gray-800">#{row.id}</span>
                <span className="font-medium text-blue-600">{row.payment_method}</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Date:</span>
                  <span className="text-gray-800">{row.payment_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Amount:</span>
                  <span className="text-gray-800">{row.amt_payable}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Advance Paid:</span>
                  <span className="text-gray-800">{row.advance_paid}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Net Total:</span>
                  <span className="text-green-600 font-medium">{row.net_total}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-6">
          <div className="text-sm text-gray-600">
            Showing {startIndex + 1} to {Math.min(startIndex + entriesPerPage, filteredData.length)} of{' '}
            {filteredData.length} entries
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:bg-gray-300 hover:bg-blue-700 transition duration-200"
            >
              Previous
            </button>
            <span className="text-sm font-medium text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:bg-gray-300 hover:bg-blue-700 transition duration-200"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentS;


// import { FaFileExcel, FaFilePdf } from 'react-icons/fa';
// import * as XLSX from 'xlsx'; // Correct import
// import jsPDF from 'jspdf';
// import 'jspdf-autotable'; // Import jsPDF autotable plugin
// import { CiSearch } from "react-icons/ci";

// function PaymentS() {
//   const [searchQuery, setSearchQuery] = useState('');
//   const [currentPage, setCurrentPage] = useState(1);
//   const [entriesPerPage, setEntriesPerPage] = useState(10);

//   const paymentData = [
//     {
//       id: 1,
//       paymentMethod: 'Credit Card',
//       paymentDate: '2023-12-01',
//       amount: 1000,
//       advancePaid: 200,
//       netTotal: 800,
//     },
//     {
//       id: 2,
//       paymentMethod: 'Cash',
//       paymentDate: '2023-12-02',
//       amount: 500,
//       advancePaid: 100,
//       netTotal: 400,
//     },
//   ];

//   const filteredData = paymentData.filter((row) => 
//     row.paymentMethod.toLowerCase().includes(searchQuery.toLowerCase()) || 
//     row.paymentDate.includes(searchQuery)
//   );

//   const startIndex = (currentPage - 1) * entriesPerPage;
//   const paginatedData = entriesPerPage === "All" ? filteredData : filteredData.slice(startIndex, startIndex + entriesPerPage);
//   const totalPages = Math.ceil(filteredData.length / entriesPerPage);

//   const handleSearch = (e) => setSearchQuery(e.target.value);

//   const handleEntriesChange = (e) => {
//     setEntriesPerPage(e.target.value === "All" ? "All" : parseInt(e.target.value, 10));
//     setCurrentPage(1);
//   };

//   const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
//   const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
//   const handlePageChange = (page) => setCurrentPage(page);

//   const handleExcelExport = () => {
//     const columns = ['Payment Method', 'Payment Date', 'Amount', 'Advance Paid', 'Net Total'];
//     const rows = paginatedData.map((row) => [
//       row.paymentMethod, 
//       row.paymentDate, 
//       row.amount, 
//       row.advancePaid, 
//       row.netTotal
//     ]);

//     const ws = XLSX.utils.aoa_to_sheet([columns, ...rows]);
//     const wb = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(wb, ws, 'Payments');
//     XLSX.writeFile(wb, 'payment-data.xlsx');
//   };

//   const handlePdfExport = () => {
//     const doc = new jsPDF();
//     const columns = ['Payment Method', 'Payment Date', 'Amount', 'Advance Paid', 'Net Total'];
//     const rows = paginatedData.map((row) => [
//       row.paymentMethod, 
//       row.paymentDate, 
//       row.amount, 
//       row.advancePaid, 
//       row.netTotal
//     ]);

//     doc.autoTable({
//       head: [columns],
//       body: rows,
//       startY: 20,
//       theme: 'grid',
//       margin: { horizontal: 10 },
//       headStyles: {
//         fillColor: [0, 123, 255],
//         textColor: 255,
//       },
//     });

//     doc.save('payment-data.pdf');
//   };

//   return (
//     <div className="container mx-auto p-6 xl:px-24">
//       {/* Top Section: Search, Excel, PDF buttons, Entries per page */}
//       <div className="flex flex-col sm:flex-row justify-between items-center mb-4 space-y-4 sm:space-y-0">
//         {/* Search Input and Buttons */}
//         <div className="flex sm:flex-row justify-between items-center mb-4 space-x-4 sm:space-x-6">
//           {/* Search Input */}
//           <div className="relative w-full sm:w-80 md:w-96">
//             <input
//               type="text"
//               placeholder="Search Staff..."
//               value={searchQuery}
//               onChange={handleSearch}
//               className="p-3 w-full rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 pl-10"
//             />
//             <CiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
//           </div>

//           {/* Excel and PDF Buttons */}
//           <div className="flex space-x-4 sm:space-x-2  ml-4">
//             <button
//               onClick={handleExcelExport}
//               className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-sm sm:text-lg py-2 px-5 rounded-full shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
//             >
//               <FaFileExcel className="text-2xl" />
//               Excel
//             </button>
//             <button
//               onClick={handlePdfExport}
//               className="bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold text-sm sm:text-lg py-2 px-5 rounded-full shadow-lg hover:shadow-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
//             >
//               <FaFilePdf className="text-2xl" />
//               PDF
//             </button>
//           </div>
//         </div>

//         {/* Entries per Page Dropdown */}
//         <div className="flex items-center space-x-2 sm:w-auto w-full justify-end">
//           <span>Show</span>
//           <select
//             value={entriesPerPage}
//             onChange={handleEntriesChange}
//             className="p-2 border border-gray-300 rounded-md"
//           >
//             <option value={10}>10</option>
//             <option value={25}>25</option>
//             <option value={50}>50</option>
//             <option value="All">All</option>
//           </select>
//           <span>entries</span>
//         </div>
//       </div>

//       {/* Payment Table */}
//       <div className="overflow-x-auto">
//         <table className="min-w-full table-auto border-collapse border border-gray-300 mb-4">
//           <thead>
//             <tr>
//               <th className="p-2 border">Payment Method</th>
//               <th className="p-2 border">Payment Date</th>
//               <th className="p-2 border">Amount</th>
//               <th className="p-2 border">Advance Paid</th>
//               <th className="p-2 border">Net Total</th>
//             </tr>
//           </thead>
//           <tbody>
//             {paginatedData.map((row, index) => (
//               <tr key={index}>
//                 <td className="p-2 border">{row.paymentMethod}</td>
//                 <td className="p-2 border">{row.paymentDate}</td>
//                 <td className="p-2 border">{row.amount}</td>
//                 <td className="p-2 border">{row.advancePaid}</td>
//                 <td className="p-2 border">{row.netTotal}</td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>

//       {/* Pagination Controls below Table */}
//       <div className="flex justify-end space-x-4 mt-4">
//         <div className="flex items-center space-x-4">
//           <button
//             onClick={handlePrevPage}
//             disabled={currentPage === 1}
//             className="px-4 py-2 bg-blue-500 text-white rounded-md"
//           >
//             Previous
//           </button>
//           <span>{`Page ${currentPage} of ${totalPages}`}</span>
//           <button
//             onClick={handleNextPage}
//             disabled={currentPage === totalPages}
//             className="px-4 py-2 bg-blue-500 text-white rounded-md"
//           >
//             Next
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default PaymentS;

